import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSportConfig } from '../hooks/useSportConfig';
import { useAuth } from '../contexts/AuthContext';
import MatchReportModal from '../components/MatchReportModal';
import {
  getFixtures,
  createFixture,
  updateFixture,
  deleteFixture,
  getTeams,
  getPlayers,
  potmIdsOf,
  getFixturesSyncConfig,
  updateFixturesSyncConfig,
  syncFixturesNow,
  getPlayCricketConfig,
  updatePlayCricketConfig,
  getPlayCricketTeams,
  syncPlayCricketNow,
  PlayCricketConfig,
  Fixture,
  FixtureInput,
  FixtureStatus,
  Team,
  Player,
  InningsOutcome,
} from '../api/client';
import './Fixtures.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const STATUS_LABELS: Record<FixtureStatus, string> = {
  scheduled: 'Scheduled',
  played: 'Played',
  postponed: 'Postponed',
  cancelled: 'Cancelled',
  abandoned: 'Abandoned',
};

const OUTCOME_LABELS: Record<InningsOutcome, string> = {
  won: 'Won', lost: 'Lost', tied: 'Tied', drawn: 'Drawn', 'no-result': 'No result',
  abandoned: 'Abandoned', conceded: 'Conceded', walkover: 'Walkover',
};

interface InningsForm { runs: string; wickets: string; overs: string; declared: boolean }
interface InningsFormState {
  battedFirst: 'us' | 'them';
  us: InningsForm;
  them: InningsForm;
  outcome: InningsOutcome;
  marginBy: '' | 'runs' | 'wickets';
  margin: string;
}
const emptyInningsForm = (): InningsFormState => ({
  battedFirst: 'us',
  us: { runs: '', wickets: '', overs: '', declared: false },
  them: { runs: '', wickets: '', overs: '', declared: false },
  outcome: 'won',
  marginBy: '',
  margin: '',
});
/** Load the stored innings (batting order) and result back into the form. */
function inningsFormFrom(f: Fixture): InningsFormState {
  const st = emptyInningsForm();
  const inn = f.innings || [];
  if (inn[0]) st.battedFirst = inn[0].side;
  for (const i of inn) {
    st[i.side] = { runs: String(i.runs), wickets: String(i.wickets), overs: i.overs === undefined ? '' : String(i.overs), declared: !!i.declared };
  }
  if (f.result) {
    st.outcome = f.result.outcome;
    st.marginBy = f.result.marginBy || '';
    st.margin = f.result.margin === undefined ? '' : String(f.result.margin);
  }
  return st;
}

const emptyForm: FixtureInput = {
  teamId: '',
  date: '',
  koTime: '',
  homeAway: 'home',
  opponent: '',
  competition: 'League',
  status: 'scheduled',
  notes: '',
};

function fmtDate(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
}

function monthLabel(date: string): string {
  const [y, m] = date.split('-').map(Number);
  return `${MONTHS[(m || 1) - 1]} ${y}`;
}

function resultOf(f: Fixture): 'W' | 'D' | 'L' | 'T' | 'NR' | null {
  // The API computes this for both result models; the fallback is for a row
  // loaded from an older API.
  if (f.summary) return f.summary.outcome;
  if (f.status !== 'played' || f.goalsFor === undefined || f.goalsAgainst === undefined) return null;
  return f.goalsFor > f.goalsAgainst ? 'W' : f.goalsFor < f.goalsAgainst ? 'L' : 'D';
}
function scoreOf(f: Fixture): string {
  if (f.summary) return f.summary.scoreline || 'W/O';
  return f.goalsFor === undefined || f.goalsAgainst === undefined ? 'W/O' : `${f.goalsFor}–${f.goalsAgainst}`;
}

/**
 * Auto-sync panel: map each team to its FA Full-Time team page and the nightly
 * job (plus "Sync now") keeps its results current. Manual fixtures are never
 * overwritten, only synced rows are managed.
 */
function SyncPanel({ teams, onSynced }: { teams: Team[]; onSynced: () => void }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [feeds, setFeeds] = useState<Array<{ teamId: string; input: string }>>([]);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getFixturesSyncConfig()
      .then((c) => {
        setEnabled(c.enabled);
        setLastSyncedAt(c.lastSyncedAt);
        setFeeds(c.feeds.map((f) => ({ teamId: f.teamId, input: f.fullTimeTeamId })));
        if (c.feeds.length || c.enabled) setOpen(true);
      })
      .catch(() => { /* first-time: no config yet */ });
  }, []);

  const cleanFeeds = () => feeds.filter((f) => f.teamId && f.input.trim())
    .map((f) => ({ teamId: f.teamId, fullTimeTeamId: f.input.trim() }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const c = await updateFixturesSyncConfig({ enabled, feeds: cleanFeeds() });
      setEnabled(c.enabled);
      setMsg('Saved.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setSyncing(true); setMsg('');
    try {
      const r = await syncFixturesNow(cleanFeeds());
      setMsg(r.ok
        ? `Synced ${r.fetched} matches, ${r.written} updated, ${r.removed} removed.`
        : `Sync error: ${r.error}`);
      setLastSyncedAt(new Date().toISOString());
      onSynced();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Sync failed');
    } finally { setSyncing(false); }
  };

  if (!open) {
    return (
      <button className="fx-sync-toggle" onClick={() => setOpen(true)}>
        ⚡ Set up auto-sync from FA Full-Time
      </button>
    );
  }

  return (
    <div className="fx-sync">
      <div className="fx-sync-head">
        <h2>Auto-sync from FA Full-Time</h2>
        <label className="fx-sync-enable">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>Sync nightly</span>
        </label>
      </div>
      <p className="fx-sync-note">
        Link each team to its FA Full-Time page, paste the page link (or its id). Results
        update automatically every night; your manually-added fixtures are never touched.
        You only link once, the same page rolls over to each new season.
      </p>

      {feeds.map((f, i) => (
        <div key={i} className="fx-sync-feed">
          <select value={f.teamId} onChange={(e) => setFeeds(feeds.map((x, j) => j === i ? { ...x, teamId: e.target.value } : x))}>
            <option value="">, team, </option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input
            type="text" placeholder="fulltime.thefa.com/displayTeam.html?id=…"
            value={f.input}
            onChange={(e) => setFeeds(feeds.map((x, j) => j === i ? { ...x, input: e.target.value } : x))}
          />
          <button className="fx-sync-rm" title="Remove" onClick={() => setFeeds(feeds.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button className="fx-sync-add" onClick={() => setFeeds([...feeds, { teamId: '', input: '' }])}>
        + Add a team feed
      </button>

      <div className="fx-sync-actions">
        <button className="btn btn-secondary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-primary" onClick={runNow} disabled={syncing || cleanFeeds().length === 0}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
        {lastSyncedAt && <span className="fx-sync-last">Last synced {new Date(lastSyncedAt).toLocaleString('en-GB')}</span>}
      </div>
      {msg && <div className="fx-sync-msg">{msg}</div>}
    </div>
  );
}

/**
 * Play-Cricket panel, cricket clubs only. The club brings its own site id and
 * API key (the ECB issues keys per club), links each Play-Cricket team to one
 * of its teams here, and the nightly job (plus "Sync now") keeps fixtures and
 * results current. Fixtures typed in by hand are never touched.
 */
function PlayCricketPanel({ teams, onSynced }: { teams: Team[]; onSynced: () => void }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState<PlayCricketConfig | null>(null);
  const [siteId, setSiteId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [pcTeams, setPcTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const adopt = (c: PlayCricketConfig) => {
    setCfg(c);
    setSiteId(c.siteId);
    setEnabled(c.enabled);
    setLinks(Object.fromEntries(c.teams.map(t => [t.pcTeamId, t.teamId])));
    if (c.teams.length) setPcTeams(c.teams.map(t => ({ id: t.pcTeamId, name: t.pcTeamName || `Team ${t.pcTeamId}` })));
  };

  useEffect(() => {
    getPlayCricketConfig()
      .then((c) => { adopt(c); if (c.siteId || c.hasKey) setOpen(true); })
      .catch(() => { /* not a cricket club with fixtures: the panel stays closed */ });
  }, []);

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label); setMsg('');
    try { await fn(); } catch (e) { setMsg(e instanceof Error ? e.message : 'Something went wrong'); }
    finally { setBusy(''); }
  };

  const linkList = () => pcTeams.map(t => ({ pcTeamId: t.id, pcTeamName: t.name, teamId: links[t.id] || '' }));

  const save = () => run('save', async () => {
    const c = await updatePlayCricketConfig({
      siteId: siteId.trim(),
      ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      ...(pcTeams.length ? { teams: linkList() } : {}),
      enabled,
    });
    adopt(c); setApiKey('');
    setMsg(enabled && !c.enabled ? 'Saved. Nightly sync needs the site id, the key and at least one linked team.' : 'Saved.');
  });

  const removeKey = () => run('save', async () => {
    adopt(await updatePlayCricketConfig({ removeKey: true }));
    setMsg('Key removed.');
  });

  const loadTeams = () => run('teams', async () => {
    const list = await getPlayCricketTeams();
    setPcTeams(list);
    // Suggest a link where the names obviously match; the admin confirms by saving.
    const next = { ...links };
    for (const t of list) {
      if (next[t.id]) continue;
      const hit = teams.find(x => x.name.trim().toLowerCase() === t.name.trim().toLowerCase());
      if (hit) next[t.id] = hit.id;
    }
    setLinks(next);
    setMsg(list.length ? `Found ${list.length} teams on Play-Cricket. Link each one you want here, then save.` : 'Play-Cricket lists no teams for that site id.');
  });

  const syncNow = () => run('sync', async () => {
    const r = await syncPlayCricketNow();
    setMsg(r.ok
      ? `Synced ${r.fetched} matches: ${r.written} updated, ${r.removed} removed${r.unmapped ? `, ${r.unmapped} skipped for teams not linked` : ''}.${r.error ? ` (${r.error})` : ''}`
      : `Sync error: ${r.error}`);
    setCfg(await getPlayCricketConfig());
    onSynced();
  });

  if (!open) {
    return (
      <button className="fx-sync-toggle" onClick={() => setOpen(true)}>
        ⚡ Pull fixtures and results from Play-Cricket
      </button>
    );
  }

  return (
    <div className="fx-sync">
      <div className="fx-sync-head">
        <h2>Fixtures from Play-Cricket</h2>
        <label className="fx-sync-enable">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>Sync nightly</span>
        </label>
      </div>
      <p className="fx-sync-note">
        Your site id is on your club&rsquo;s Play-Cricket homepage. The API key comes from the
        Play-Cricket help desk, once your club&rsquo;s main admin has signed their agreement.
        We keep the key encrypted and never show it again. Fixtures you add here by hand are
        never touched, and match reports, Player of the Match and photos stay on synced matches.
      </p>

      <div className="fx-pc-row">
        <label>Site id
          <input type="text" inputMode="numeric" placeholder="e.g. 1234" value={siteId} onChange={(e) => setSiteId(e.target.value)} />
        </label>
        <label>API key
          <input type="password" autoComplete="off"
            placeholder={cfg?.hasKey ? 'Saved. Enter a new one to replace it' : 'Paste your Play-Cricket API key'}
            value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        </label>
        {cfg?.hasKey && <button className="fx-pc-link" onClick={removeKey} disabled={!!busy}>Remove key</button>}
      </div>

      {pcTeams.length > 0 && (
        <div className="fx-pc-teams">
          {pcTeams.map(t => (
            <div key={t.id} className="fx-sync-feed">
              <span className="fx-pc-name">{t.name}</span>
              <select value={links[t.id] || ''} onChange={(e) => setLinks({ ...links, [t.id]: e.target.value })}>
                <option value="">Don&rsquo;t sync</option>
                {teams.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}

      <div className="fx-sync-actions">
        <button className="btn btn-secondary" onClick={save} disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
        <button className="btn btn-secondary" onClick={loadTeams} disabled={!!busy || !cfg?.siteId || !cfg?.hasKey}>
          {busy === 'teams' ? 'Loading…' : 'Load Play-Cricket teams'}
        </button>
        <button className="btn btn-primary" onClick={syncNow} disabled={!!busy || !cfg?.teams.length}>
          {busy === 'sync' ? 'Syncing…' : 'Sync now'}
        </button>
        {cfg?.lastSyncedAt && <span className="fx-sync-last">Last synced {new Date(cfg.lastSyncedAt).toLocaleString('en-GB')}{cfg.lastStatus && cfg.lastStatus !== 'ok' ? `, ${cfg.lastStatus}` : ''}</span>}
      </div>
      {msg && <div className="fx-sync-msg">{msg}</div>}
    </div>
  );
}

function Fixtures() {
  const sportConfig = useSportConfig();
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Fixture | null>(null);
  // Club + Pro get match reports by paying; the old flag still grants it to a
  // Free club an operator has switched on by hand. Free sees the button and is
  // told what it is, rather than never learning the feature exists.
  const { plan, features } = useAuth();
  const matchReportsOn = plan === 'club' || plan === 'pro' || features?.matchReports === true;
  const [reportLocked, setReportLocked] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [openMonths, setOpenMonths] = useState<Record<string, boolean>>({});
  // The form edits one award. A match with several is managed in the report.
  const [potmId, setPotmId] = useState('');
  const [potmTouched, setPotmTouched] = useState(false);
  const [reportFor, setReportFor] = useState<Fixture | null>(null);
  const [reportMsg, setReportMsg] = useState('');
  const [form, setForm] = useState<FixtureInput>(emptyForm);
  const [goals, setGoals] = useState({ for: '', against: '' });
  const [inn, setInn] = useState<InningsFormState>(emptyInningsForm());
  const isInnings = sportConfig.resultModel === 'innings';

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [fx, ts, ps] = await Promise.all([getFixtures(), getTeams().catch(() => []), getPlayers().catch(() => [])]);
      setFixtures(fx);
      setTeams(ts);
      setPlayers(ps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fixtures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setGoals({ for: '', against: '' });
    setInn(emptyInningsForm());
    setPotmId(''); setPotmTouched(false);
    setModalOpen(true);
  };

  const openEdit = (f: Fixture, asResult = false) => {
    setEditing(f);
    setForm({
      teamId: f.teamId || '',
      date: f.date,
      koTime: f.koTime || '',
      homeAway: f.homeAway,
      opponent: f.opponent,
      competition: f.competition,
      status: asResult ? 'played' : f.status,
      notes: f.notes || '',
    });
    setGoals({
      for: f.goalsFor !== undefined ? String(f.goalsFor) : '',
      against: f.goalsAgainst !== undefined ? String(f.goalsAgainst) : '',
    });
    setInn(inningsFormFrom(f));
    setPotmId(potmIdsOf(f)[0] || ''); setPotmTouched(false);
    setModalOpen(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload: FixtureInput = { ...form };
    // Only an award the admin actually changed is sent, so a save never
    // silently drops awards made in the match report.
    if (potmTouched && form.status === 'played') payload.potmPlayerIds = potmId ? [potmId] : [];
    if (form.status === 'played' && isInnings) {
      const order: Array<'us' | 'them'> = inn.battedFirst === 'us' ? ['us', 'them'] : ['them', 'us'];
      payload.innings = order.map(side => ({
        side,
        runs: Number(inn[side].runs),
        wickets: Number(inn[side].wickets),
        ...(inn[side].overs.trim() ? { overs: Number(inn[side].overs) } : {}),
        ...(inn[side].declared ? { declared: true } : {}),
      }));
      payload.result = {
        outcome: inn.outcome,
        ...(inn.marginBy && inn.margin.trim() ? { marginBy: inn.marginBy, margin: Number(inn.margin) } : {}),
      };
    } else if (form.status === 'played') {
      payload.goalsFor = Number(goals.for);
      payload.goalsAgainst = Number(goals.against);
    }
    try {
      if (editing) {
        await updateFixture(editing.fixtureId, payload);
      } else {
        await createFixture(payload);
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save fixture');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (f: Fixture) => {
    if (!window.confirm(`Delete ${f.homeAway === 'home' ? 'vs' : 'at'} ${f.opponent} on ${f.date}?`)) return;
    try {
      await deleteFixture(f.fixtureId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete fixture');
    }
  };

  const teamName = (id?: string) => teams.find(t => t.id === id)?.name || '';

  // Newest first for the admin: the next fixture / latest result is the working end
  const sorted = [...fixtures].sort((a, b) =>
    b.date.localeCompare(a.date) || (b.koTime || '').localeCompare(a.koTime || ''));

  const groups: Array<{ label: string; key: string; rows: Fixture[] }> = [];
  let lastGroup: { label: string; key: string; rows: Fixture[] } | undefined;
  for (const f of sorted) {
    const label = monthLabel(f.date);
    if (!lastGroup || lastGroup.label !== label) {
      lastGroup = { label, key: f.date.slice(0, 7), rows: [] };
      groups.push(lastGroup);
    }
    lastGroup.rows.push(f);
  }
  // Newest first puts months that have not happened yet at the top, so a club
  // scrolled past them to reach the result it came to enter. Future months start
  // folded; this month and everything behind it stay open; a click overrides. A
  // club whose season has not started has only future months: the soonest one
  // stays open, so the page never opens looking empty.
  const thisMonth = new Date().toISOString().slice(0, 7);
  const soonest = groups.length && groups.every(g => g.key > thisMonth) ? groups[groups.length - 1]!.key : '';
  const isOpen = (g: { key: string }) => openMonths[g.key] ?? (g.key <= thisMonth || g.key === soonest);
  const toggleMonth = (key: string, open: boolean) => setOpenMonths(prev => ({ ...prev, [key]: !open }));

  const playerName = (id?: string) => {
    const p = players.find(x => x.playerId === id);
    return p ? `${p.firstName} ${p.lastName || ''}`.trim() : '';
  };
  const potmNames = (f: Fixture) => potmIdsOf(f).map(playerName).filter(Boolean);
  const editingPotms = editing ? potmIdsOf(editing) : [];
  const squad = players
    .filter(p => (p.teamId === form.teamId && p.status !== 'archived') || p.playerId === potmId)
    .sort((a, b) => a.firstName.localeCompare(b.firstName));
  const potmPlayer = players.find(p => p.playerId === potmId);
  const potmHint = !potmPlayer ? ''
    : teams.find(t => t.id === form.teamId)?.hideSquad
      ? "This won't show on your website: this team's squad is hidden."
      : potmPlayer.status === 'archived' || !potmPlayer.publicFields?.showOnSquadPage
        ? `This won't show on your website because ${potmPlayer.firstName}'s profile isn't public.`
        : '';

  return (
    <div className="fx-page">
      <div className="fx-header">
        <div>
          <h1>Fixtures &amp; Results</h1>
          <p className="fx-sub">
            Matches shown on your public site. Enter results after the final whistle,
            season grouping and form work themselves out.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Add Fixture</button>
      </div>

      {sportConfig.id === 'cricket' && <PlayCricketPanel teams={teams} onSynced={load} />}
      {/* FA Full-Time only means anything to a football club. */}
      {sportConfig.id === 'football' && <SyncPanel teams={teams} onSynced={load} />}

      {error && <div className="fx-error">{error}</div>}

      {reportMsg && <div className="fx-report-msg">{reportMsg}</div>}
      {reportFor && (
        <MatchReportModal
          fixtureId={reportFor.fixtureId}
          onClose={() => setReportFor(null)}
          onSaved={(m) => { setReportMsg(m); void load(); }}
        />
      )}

      {loading ? (
        <p className="fx-status">Loading fixtures...</p>
      ) : fixtures.length === 0 ? (
        <div className="fx-empty">
          <p>No fixtures yet. Add your first match and it appears on the website instantly.</p>
          <button className="btn btn-primary" onClick={openCreate}>+ Add Fixture</button>
        </div>
      ) : (
        groups.map(group => (
          <div key={group.label} className="fx-group">
            <button type="button" className="fx-group-heading" aria-expanded={isOpen(group)}
              onClick={() => toggleMonth(group.key, isOpen(group))}>
              <span className={`fx-group-caret ${isOpen(group) ? 'is-open' : ''}`} aria-hidden="true">›</span>
              {group.label}
              <span className="fx-group-count">{group.rows.length}</span>
            </button>
            {isOpen(group) && (
            <div className="fx-list">
              {group.rows.map(f => {
                const r = resultOf(f);
                return (
                  <div key={f.fixtureId} className={`fx-card ${f.status === 'cancelled' ? 'fx-card-off' : ''}`}>
                    <div className="fx-when">
                      <span className="fx-date">{fmtDate(f.date)}</span>
                      <span className="fx-ko">{f.koTime || '-'}</span>
                    </div>
                    <div className="fx-match">
                      <span className="fx-opponent">
                        {f.homeAway === 'home' ? 'vs' : 'at'} {f.opponent}
                      </span>
                      <span className="fx-meta">
                        {f.competition}
                        {f.teamId && teamName(f.teamId) ? ` · ${teamName(f.teamId)}` : ''}
                        {f.notes ? ` · ${f.notes}` : ''}
                        {f.status === 'played' && potmNames(f).length ? ` · ★ ${potmNames(f).join(', ')}` : ''}
                      </span>
                    </div>
                    <div className="fx-outcome">
                      {f.status === 'played' ? (
                        <>
                          <span className="fx-score" title={f.summary?.description || undefined}>
                            {scoreOf(f)}
                          </span>
                          {r && <span className={`fx-chip fx-chip-${r.toLowerCase()}`}>{r}</span>}
                        </>
                      ) : f.status === 'scheduled' ? (
                        /* Straight into the combined editor when the club can
                           report: the score and who played are one event, and
                           splitting them meant the second half got skipped.
                           Falls back to the plain fixture form otherwise. */
                        <button className="btn btn-secondary fx-result-btn"
                          onClick={() => (matchReportsOn && f.teamId && !isInnings ? setReportFor(f) : openEdit(f, true))}>
                          Enter result
                        </button>
                      ) : (
                        <span className="fx-status-tag">{STATUS_LABELS[f.status]}</span>
                      )}
                    </div>
                    <div className="fx-actions">
                      {f.status === 'played' && f.teamId && (
                        matchReportsOn ? (
                          <button className="fx-icon-btn" title="Match report"
                                  onClick={() => setReportFor(f)}>📋</button>
                        ) : (
                          <button className="fx-icon-btn fx-icon-locked" title="Match report, on Club and Pro"
                                  onClick={() => setReportLocked(true)}>📋</button>
                        )
                      )}
                      <button className="fx-icon-btn" title="Edit" onClick={() => openEdit(f)}>✏️</button>
                      <button className="fx-icon-btn" title="Delete" onClick={() => handleDelete(f)}>🗑️</button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        ))
      )}

      {/* Free clubs see the report button and learn what it does. The server
          404s them either way. */}
      {reportLocked && (
        <div className="fx-modal-overlay" onClick={() => setReportLocked(false)}>
          <div className="fx-modal fx-locked-modal" onClick={e => e.stopPropagation()}>
            <h2>Match reports</h2>
            <p>
              Record who played, their {sportConfig.stats?.keys?.slice(1, 3).map(k => k.label.toLowerCase()).join(' and ') || 'stats'} and
              who was player of the match. Appearances and stats then build up on each player's
              profile across the season, and every played match gets its own page on your website.
            </p>
            <p className="fx-locked-note">Included on Club and Pro.</p>
            <div className="fx-modal-actions">
              <button className="btn btn-secondary" onClick={() => setReportLocked(false)}>Not now</button>
              <Link className="btn btn-primary" to="/upgrade">See the plans</Link>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fx-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="fx-modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Edit Fixture' : 'Add Fixture'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="fx-form-row">
                <label className="fx-field">
                  <span>Date *</span>
                  <input type="date" required value={form.date}
                    onChange={e => setForm({ ...form, date: e.target.value })} />
                </label>
                <label className="fx-field">
                  <span>Kick-off</span>
                  <input type="time" value={form.koTime}
                    onChange={e => setForm({ ...form, koTime: e.target.value })} />
                </label>
              </div>

              <div className="fx-form-row">
                <label className="fx-field">
                  <span>Home / Away</span>
                  <select value={form.homeAway}
                    onChange={e => setForm({ ...form, homeAway: e.target.value as 'home' | 'away' })}>
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                  </select>
                </label>
                <label className="fx-field">
                  <span>Competition</span>
                  <input type="text" maxLength={40} value={form.competition}
                    placeholder="League / Cup / Friendly"
                    onChange={e => setForm({ ...form, competition: e.target.value })} />
                </label>
              </div>

              <label className="fx-field">
                <span>Opponent *</span>
                <input type="text" required maxLength={80} value={form.opponent}
                  onChange={e => setForm({ ...form, opponent: e.target.value })} />
              </label>

              {teams.length > 0 && (
                <label className="fx-field">
                  <span>Team</span>
                  <select value={form.teamId}
                    onChange={e => { setForm({ ...form, teamId: e.target.value }); if (potmId) { setPotmId(''); setPotmTouched(true); } }}>
                    <option value="">, Whole club / first team, </option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
              )}

              <div className="fx-form-row">
                <label className="fx-field">
                  <span>Status</span>
                  <select value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value as FixtureStatus })}>
                    {(Object.keys(STATUS_LABELS) as FixtureStatus[]).map(s => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                </label>
                {form.status === 'played' && isInnings && (
                  <div className="fx-innings">
                    <label className="fx-field">
                      <span>Batted first</span>
                      <select value={inn.battedFirst}
                        onChange={e => setInn({ ...inn, battedFirst: e.target.value as 'us' | 'them' })}>
                        <option value="us">Us</option>
                        <option value="them">Them</option>
                      </select>
                    </label>
                    {(['us', 'them'] as const).map(side => (
                      <div key={side} className="fx-innings-row">
                        <span className="fx-innings-side">{side === 'us' ? 'Us' : 'Them'}</span>
                        <label className="fx-field fx-field-goal">
                          <span>Runs</span>
                          <input type="number" min={0} max={999} required value={inn[side].runs}
                            onChange={e => setInn({ ...inn, [side]: { ...inn[side], runs: e.target.value } })} />
                        </label>
                        <label className="fx-field fx-field-goal">
                          <span>Wkts</span>
                          <input type="number" min={0} max={10} required value={inn[side].wickets}
                            onChange={e => setInn({ ...inn, [side]: { ...inn[side], wickets: e.target.value } })} />
                        </label>
                        <label className="fx-field fx-field-goal">
                          <span>Overs</span>
                          <input type="number" min={0} step={0.1} placeholder="45.3" value={inn[side].overs}
                            onChange={e => setInn({ ...inn, [side]: { ...inn[side], overs: e.target.value } })} />
                        </label>
                        <label className="fx-innings-dec">
                          <input type="checkbox" checked={inn[side].declared}
                            onChange={e => setInn({ ...inn, [side]: { ...inn[side], declared: e.target.checked } })} />
                          dec
                        </label>
                      </div>
                    ))}
                    <div className="fx-form-row">
                      <label className="fx-field">
                        <span>Result</span>
                        <select value={inn.outcome}
                          onChange={e => setInn({ ...inn, outcome: e.target.value as InningsOutcome })}>
                          {(Object.keys(OUTCOME_LABELS) as InningsOutcome[]).map(o => (
                            <option key={o} value={o}>{OUTCOME_LABELS[o]}</option>
                          ))}
                        </select>
                      </label>
                      {(inn.outcome === 'won' || inn.outcome === 'lost') && (
                        <>
                          <label className="fx-field">
                            <span>By</span>
                            <select value={inn.marginBy}
                              onChange={e => setInn({ ...inn, marginBy: e.target.value as InningsFormState['marginBy'] })}>
                              <option value="">Work it out from the innings</option>
                              <option value="runs">Runs</option>
                              <option value="wickets">Wickets</option>
                            </select>
                          </label>
                          {inn.marginBy && (
                            <label className="fx-field fx-field-goal">
                              <span>Margin</span>
                              <input type="number" min={0} max={inn.marginBy === 'wickets' ? 10 : 999} required value={inn.margin}
                                onChange={e => setInn({ ...inn, margin: e.target.value })} />
                            </label>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                {form.status === 'played' && !isInnings && (
                  <div className="fx-scores">
                    <label className="fx-field fx-field-goal">
                      <span>Us</span>
                      <input type="number" min={0} max={sportConfig.score.max} required value={goals.for}
                        onChange={e => setGoals({ ...goals, for: e.target.value })} />
                    </label>
                    <span className="fx-score-dash">–</span>
                    <label className="fx-field fx-field-goal">
                      <span>Them</span>
                      <input type="number" min={0} max={sportConfig.score.max} required value={goals.against}
                        onChange={e => setGoals({ ...goals, against: e.target.value })} />
                    </label>
                  </div>
                )}
              </div>

              {form.status === 'played' && (editingPotms.length > 1 ? (
                <p className="fx-potm-hint">
                  Players of the Match: {editingPotms.map(playerName).filter(Boolean).join(', ')}. Change them in the match report.
                </p>
              ) : (
                <label className="fx-field">
                  <span>Player of the Match</span>
                  <select value={potmId} disabled={!form.teamId || squad.length === 0}
                    onChange={e => { setPotmId(e.target.value); setPotmTouched(true); }}>
                    <option value="">
                      {!form.teamId ? 'Choose a team first' : squad.length === 0 ? 'No players on this team yet' : 'None'}
                    </option>
                    {squad.map(p => (
                      <option key={p.playerId} value={p.playerId}>{`${p.firstName} ${p.lastName || ''}`.trim()}</option>
                    ))}
                  </select>
                  {potmHint && <small className="fx-potm-hint">{potmHint}</small>}
                </label>
              ))}

              <label className="fx-field">
                <span>Notes</span>
                <input type="text" maxLength={120} value={form.notes}
                  placeholder="e.g. walkover, ground change"
                  onChange={e => setForm({ ...form, notes: e.target.value })} />
              </label>

              <div className="fx-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Fixture'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Fixtures;
